import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, ArrowRight } from "lucide-react";
import StatusFormDialog from "./StatusFormDialog";
import WorkflowTransitionFormDialog from "./WorkflowTransitionFormDialog";
import { statusService, statusWorkflowService, type StatusEntityType } from "@/services";

interface StatusWorkflowGridProps {
  entityType: string;
  companyId: string;
}

interface Status {
  id: string;
  name: string;
  unique_name: string;
  seq_num: number;
  subtext: string | null;
  tooltip: string | null;
  readonly: boolean | null;
  created: string;
  created_by: string | null;
  modified: string;
  updated_by: string | null;
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
}

interface WorkflowTransition {
  id: string;
  from_status_id: string;
  to_status_id: string;
  transition_label: string;
  seq_num: number;
  subtext: string | null;
  tooltip: string | null;
  confirmation_msg: string | null;
  comments: 'no' | 'optional' | 'required';
  created: string;
  created_by: string | null;
  modified: string;
  updated_by: string | null;
  from_status?: { id: string; name: string } | null;
  to_status?: { id: string; name: string } | null;
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
}

export default function StatusWorkflowGrid({ entityType, companyId }: StatusWorkflowGridProps) {
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowTransition | null>(null);
  const [workflowFromStatus, setWorkflowFromStatus] = useState<{ id: string; name: string } | null>(null);

  const { data: statuses = [], isLoading: statusesLoading } = useQuery({
    queryKey: ["statuses", companyId, entityType],
    queryFn: async () => {
      const result = await statusService.listWithAuditInfo(entityType as StatusEntityType, companyId);
      if (result.error) throw result.error;
      return result.data as Status[];
    },
    enabled: !!companyId,
  });

  const statusIds = statuses.map((s) => s.id);

  const { data: workflows = [], isLoading: workflowsLoading } = useQuery({
    queryKey: ["status_workflows", companyId, entityType],
    queryFn: async () => {
      if (statusIds.length === 0) return [];
      const result = await statusWorkflowService.listForStatusIds(companyId, statusIds);
      if (result.error) throw result.error;
      return result.data as WorkflowTransition[];
    },
    enabled: !!companyId && statusIds.length > 0,
  });

  const getWorkflowsForStatus = (statusId: string) => {
    return workflows.filter((w) => w.from_status_id === statusId).sort((a, b) => a.seq_num - b.seq_num);
  };

  const handleEditStatus = (status: Status) => {
    setSelectedStatus(status);
    setStatusDialogOpen(true);
  };

  const handleCreateStatus = () => {
    setSelectedStatus(null);
    setStatusDialogOpen(true);
  };

  const handleEditWorkflow = (workflow: WorkflowTransition) => {
    const fromStatus = statuses.find((s) => s.id === workflow.from_status_id);
    if (fromStatus) {
      setWorkflowFromStatus({ id: fromStatus.id, name: fromStatus.name });
      setSelectedWorkflow(workflow);
      setWorkflowDialogOpen(true);
    }
  };

  const handleCreateWorkflow = (fromStatus: Status) => {
    setWorkflowFromStatus({ id: fromStatus.id, name: fromStatus.name });
    setSelectedWorkflow(null);
    setWorkflowDialogOpen(true);
  };

  const nextStatusSeqNum = statuses.length > 0 ? Math.max(...statuses.map((s) => s.seq_num)) + 1 : 1;

  const getNextWorkflowSeqNum = (statusId: string) => {
    const statusWorkflows = getWorkflowsForStatus(statusId);
    return statusWorkflows.length > 0 ? Math.max(...statusWorkflows.map((w) => w.seq_num)) + 1 : 1;
  };

  const isLoading = statusesLoading || workflowsLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-4">
            <Skeleton className="h-16 w-32" />
            <Skeleton className="h-16 w-32" />
            <Skeleton className="h-16 w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (statuses.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No statuses defined for this entity type yet.</p>
        <Button onClick={handleCreateStatus} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />
          Create First Status
        </Button>
        <StatusFormDialog
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
          status={selectedStatus}
          entityType={entityType}
          companyId={companyId}
          nextSeqNum={1}
          availableStatuses={statuses.map((s) => ({ id: s.id, name: s.name, seq_num: s.seq_num }))}
        />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="space-y-3">
          {statuses.map((status) => {
            const statusWorkflows = getWorkflowsForStatus(status.id);

            return (
              <div key={status.id} className="flex items-start gap-3 flex-wrap">
                {/* Status Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-[130px] w-[200px] py-2 px-3 flex flex-col items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-amber-400 hover:border-amber-500"
                      onClick={() => handleEditStatus(status)}
                    >
                      <span className="font-semibold text-center text-wrap leading-tight">{status.name}</span>
                      {status.subtext && (
                        <span className="text-xs opacity-80 mt-0.5 text-center text-wrap leading-tight">
                          {status.subtext}
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  {status.tooltip && (
                    <TooltipContent>
                      <p>{status.tooltip}</p>
                    </TooltipContent>
                  )}
                </Tooltip>

                {/* Arrow indicator */}
                {(statusWorkflows.length > 0 || true) && (
                  <div className="flex items-center self-center">
                    <ArrowRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}

                {/* Workflow Transition Buttons */}
                {statusWorkflows.map((workflow) => (
                  <Tooltip key={workflow.id}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-[130px] w-[200px] py-2 px-3 flex flex-col items-center justify-center bg-orange-500 hover:bg-orange-600 text-white border-2 border-orange-600 hover:border-orange-700"
                        onClick={() => handleEditWorkflow(workflow)}
                      >
                        <span className="font-semibold text-center text-wrap leading-tight">
                          {workflow.transition_label}
                        </span>
                        {workflow.subtext && (
                          <span className="text-xs opacity-80 mt-0.5 text-center text-wrap leading-tight">
                            {workflow.subtext}
                          </span>
                        )}
                        {workflow.to_status && (
                          <span className="text-xs opacity-70 mt-1 text-center text-wrap leading-tight">
                            → {workflow.to_status.name}
                          </span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    {workflow.tooltip && (
                      <TooltipContent>
                        <p>{workflow.tooltip}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}

                {/* Create New Transition Button */}
                <Button
                  variant="outline"
                  className="h-[130px] w-[200px] py-2 px-3 flex flex-col items-center justify-center bg-cyan-600 hover:bg-cyan-700 text-white border-2 border-cyan-700 hover:border-cyan-800"
                  onClick={() => handleCreateWorkflow(status)}
                >
                  <Plus className="h-4 w-4 mb-1" />
                  <span className="font-semibold text-sm text-center">Create New</span>
                  <span className="text-xs opacity-80">Transition</span>
                </Button>
              </div>
            );
          })}
        </div>

        {/* Create New Status Button */}
        <div className="pt-4 border-t border-border">
          <Button onClick={handleCreateStatus} className="bg-orange-500 hover:bg-orange-600 border-2 border-orange-600">
            <Plus className="h-4 w-4 mr-2" />
            Create New Status
          </Button>
        </div>

        {/* Status Dialog */}
        <StatusFormDialog
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
          status={selectedStatus}
          entityType={entityType}
          companyId={companyId}
          nextSeqNum={nextStatusSeqNum}
          availableStatuses={statuses.map((s) => ({ id: s.id, name: s.name, seq_num: s.seq_num }))}
        />

        {/* Workflow Transition Dialog */}
        {workflowFromStatus && (
          <WorkflowTransitionFormDialog
            open={workflowDialogOpen}
            onOpenChange={setWorkflowDialogOpen}
            workflow={selectedWorkflow}
            fromStatus={workflowFromStatus}
            availableStatuses={statuses.map((s) => ({ id: s.id, name: s.name }))}
            companyId={companyId}
            entityType={entityType}
            nextSeqNum={getNextWorkflowSeqNum(workflowFromStatus.id)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
